(() => {
  if (window.Area27VimeoPlayer) {
    window.Area27VimeoPlayer.refresh();
    return;
  }

  const PLAYER_HOST = 'player.vimeo.com';
  const PLAYER_ORIGIN = 'https://player.vimeo.com';
  const FRAME_SELECTOR = 'iframe[src*="player.vimeo.com/video/"], iframe[data-src*="player.vimeo.com/video/"]';
  const LINK_SELECTOR = 'a[href^="https://vimeo.com/"], a[href^="http://vimeo.com/"]';
  const preparedFrames = new WeakSet();
  const preparedLinks = new WeakSet();
  let modal;
  let modalFrame;
  let lastTrigger;

  const injectStyles = () => {
    if (document.getElementById('a27-vimeo-player-styles')) return;
    const style = document.createElement('style');
    style.id = 'a27-vimeo-player-styles';
    style.textContent = `
      .a27-vimeo-open-layer {
        position: absolute;
        inset: 0;
        z-index: 12;
        display: block;
        width: 100%;
        height: 100%;
        border: 0;
        padding: 0;
        background: transparent;
        color: transparent;
        cursor: pointer;
      }

      .a27-vimeo-open-layer:focus-visible {
        outline: 2px solid #18cfe2;
        outline-offset: -4px;
      }

      .a27-vimeo-modal {
        position: fixed;
        inset: 0;
        z-index: 2147483647;
        display: none;
        align-items: center;
        justify-content: center;
        padding: clamp(16px, 3vw, 42px);
        background: rgba(0, 0, 0, .94);
      }

      .a27-vimeo-modal.is-open {
        display: flex;
      }

      .a27-vimeo-modal__dialog {
        position: relative;
        width: min(1600px, 100%);
        aspect-ratio: 16 / 9;
        max-height: calc(100vh - 84px);
        background: #000;
        box-shadow: 0 0 0 1px rgba(255,255,255,.16), 0 24px 90px rgba(0,0,0,.7);
      }

      .a27-vimeo-modal__dialog iframe {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        border: 0;
      }

      .a27-vimeo-modal__close {
        position: fixed;
        top: 14px;
        right: 14px;
        z-index: 2;
        display: inline-flex;
        width: 44px;
        height: 44px;
        align-items: center;
        justify-content: center;
        border: 1px solid rgba(255,255,255,.5);
        border-radius: 0;
        background: rgba(0,0,0,.72);
        color: #fff;
        cursor: pointer;
        font: 900 28px/1 Arial, sans-serif;
      }

      .a27-vimeo-modal__close:hover,
      .a27-vimeo-modal__close:focus-visible {
        border-color: #d2a246;
        color: #d2a246;
        outline: none;
      }

      html.a27-vimeo-modal-open,
      html.a27-vimeo-modal-open body {
        overflow: hidden;
      }
    `;
    document.head.appendChild(style);
  };

  const isVimeoPlayerUrl = (url) => {
    return url && url.hostname === PLAYER_HOST && url.pathname.startsWith('/video/');
  };

  const parseUrl = (raw) => {
    if (!raw) return null;
    try {
      return new URL(String(raw).replace(/&amp;/g, '&'), window.location.href);
    } catch (_) {
      return null;
    }
  };

  const normalizePlayerUrl = (raw, { modalMode = false } = {}) => {
    const url = parseUrl(raw);
    if (!isVimeoPlayerUrl(url)) return raw || '';

    url.protocol = 'https:';
    url.searchParams.set('autoplay', '1');
    url.searchParams.set('loop', '1');
    url.searchParams.set('autopause', '0');
    url.searchParams.set('playsinline', '1');
    url.searchParams.set('title', '0');
    url.searchParams.set('byline', '0');
    url.searchParams.set('portrait', '0');
    url.searchParams.set('badge', '0');
    url.searchParams.set('dnt', '1');

    if (modalMode) {
      url.searchParams.delete('background');
      url.searchParams.set('muted', '0');
      url.searchParams.delete('mute');
    } else {
      url.searchParams.set('muted', '1');
      url.searchParams.set('mute', '1');
    }

    return url.toString();
  };

  const playerUrlFromVimeoLink = (href) => {
    const url = parseUrl(href);
    if (!url || url.hostname !== 'vimeo.com') return '';
    const parts = url.pathname.split('/').filter(Boolean);
    if (!parts.length || !/^\d+$/.test(parts[0])) return '';
    const player = new URL(`https://player.vimeo.com/video/${parts[0]}`);
    if (parts[1]) player.searchParams.set('h', parts[1]);
    return normalizePlayerUrl(player.toString(), { modalMode: true });
  };

  const setAllow = (iframe) => {
    const required = ['autoplay', 'fullscreen', 'picture-in-picture', 'clipboard-write', 'encrypted-media', 'web-share'];
    const current = (iframe.getAttribute('allow') || '')
      .split(';')
      .map((part) => part.trim())
      .filter(Boolean);
    required.forEach((item) => {
      if (!current.some((part) => part.toLowerCase() === item)) current.push(item);
    });
    iframe.setAttribute('allow', current.join('; '));
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('referrerpolicy', iframe.getAttribute('referrerpolicy') || 'strict-origin-when-cross-origin');
    if (!iframe.hasAttribute('loading')) iframe.setAttribute('loading', 'lazy');
  };

  const postMethod = (iframe, method, value) => {
    if (!iframe || !iframe.contentWindow) return;
    const message = value === undefined ? { method } : { method, value };
    iframe.contentWindow.postMessage(JSON.stringify(message), PLAYER_ORIGIN);
  };

  const ensureModal = () => {
    if (modal) return;
    modal = document.createElement('div');
    modal.className = 'a27-vimeo-modal';
    modal.setAttribute('data-a27-vimeo-modal', '');
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <button class="a27-vimeo-modal__close" type="button" aria-label="Close video">&times;</button>
      <div class="a27-vimeo-modal__dialog" role="dialog" aria-modal="true" aria-label="Area 27 video">
        <iframe title="Area 27 video" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
      </div>
    `;
    document.body.appendChild(modal);
    modalFrame = modal.querySelector('iframe');
    modal.querySelector('.a27-vimeo-modal__close')?.addEventListener('click', closeModal);
    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    });
  };

  const requestModalFullscreen = () => {
    const target = modal.querySelector('.a27-vimeo-modal__dialog') || modal;
    if (target.requestFullscreen) {
      target.requestFullscreen().catch(() => {});
    } else if (target.webkitRequestFullscreen) {
      try { target.webkitRequestFullscreen(); } catch (_) {}
    }
  };

  const openModal = (src, title, trigger) => {
    const normalized = normalizePlayerUrl(src, { modalMode: true });
    if (!normalized) return;

    ensureModal();
    lastTrigger = trigger || document.activeElement;
    modalFrame.title = title || 'Area 27 video';
    modalFrame.src = normalized;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('a27-vimeo-modal-open');
    window.setTimeout(requestModalFullscreen, 80);
    window.setTimeout(() => modal.querySelector('.a27-vimeo-modal__close')?.focus(), 120);
  };

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('a27-vimeo-modal-open');
    if (modalFrame) modalFrame.removeAttribute('src');
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      window.setTimeout(() => lastTrigger.focus(), 40);
    }
  }

  const addClickLayer = (iframe) => {
    if (iframe.closest('[data-a27-vimeo-modal]')) return;
    const parent = iframe.parentElement;
    if (!parent || parent.querySelector(':scope > .a27-vimeo-open-layer')) return;

    const computed = window.getComputedStyle(parent);
    if (computed.position === 'static') parent.style.position = 'relative';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'a27-vimeo-open-layer';
    button.setAttribute('aria-label', `Open ${iframe.title || 'Area 27 video'} full screen`);
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const src = iframe.getAttribute('src') || iframe.getAttribute('data-src') || '';
      postMethod(iframe, 'pause');
      openModal(src, iframe.title, button);
    });
    parent.appendChild(button);
  };

  const prepareFrame = (iframe) => {
    if (!(iframe instanceof HTMLIFrameElement) || preparedFrames.has(iframe)) return;
    const raw = iframe.getAttribute('src') || iframe.getAttribute('data-src');
    const url = parseUrl(raw);
    if (!isVimeoPlayerUrl(url)) return;

    preparedFrames.add(iframe);
    iframe.dataset.a27VimeoPrepared = 'true';
    setAllow(iframe);

    const srcAttr = iframe.getAttribute('src') ? 'src' : 'data-src';
    const normalized = normalizePlayerUrl(raw);
    if (iframe.getAttribute(srcAttr) !== normalized) iframe.setAttribute(srcAttr, normalized);

    iframe.addEventListener('load', () => {
      postMethod(iframe, 'setVolume', 0);
      postMethod(iframe, 'play');
    });

    addClickLayer(iframe);
  };

  const prepareVimeoLink = (link) => {
    if (!(link instanceof HTMLAnchorElement) || preparedLinks.has(link)) return;
    const playerSrc = playerUrlFromVimeoLink(link.href);
    if (!playerSrc) return;

    preparedLinks.add(link);
    link.dataset.a27VimeoLink = 'true';
    link.removeAttribute('target');
    link.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      openModal(playerSrc, link.textContent.trim() || 'Area 27 video', link);
    });
  };

  const refresh = (root = document) => {
    injectStyles();
    if (root instanceof HTMLIFrameElement) prepareFrame(root);
    if (root instanceof HTMLAnchorElement) prepareVimeoLink(root);
    root.querySelectorAll?.(FRAME_SELECTOR).forEach(prepareFrame);
    root.querySelectorAll?.(LINK_SELECTOR).forEach(prepareVimeoLink);
  };

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) refresh(node);
      });
      if (mutation.type === 'attributes') refresh(mutation.target);
    });
  });

  window.Area27VimeoPlayer = { refresh, openModal, closeModal };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh());
  } else {
    refresh();
  }

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'data-src', 'href']
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeModal();
  });

  document.addEventListener('shopify:section:load', () => refresh());
})();
